import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft, Trophy, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import { useLeaderboard, useLeaderboardGames } from '../hooks';

const backToDashboardLink = (
  <Link
    to="/a/arche_game_rewards"
    className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
  >
    <ArrowLeft className="h-4 w-4 mr-1" />
    Back to Dashboard
  </Link>
);

export function LeaderboardPage() {
  const { user } = useAuth();
  const { games, loading: gamesLoading, error: gamesError } = useLeaderboardGames();
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  // Auto-select first game when games load
  const effectiveGameId = selectedGameId ?? (games.length > 0 ? games[0].gameConfigId : null);

  // Default event for the selected game
  const effectiveEvent = selectedEvent ?? 'default';

  const {
    data: leaderboard,
    loading: lbLoading,
    error: lbError,
  } = useLeaderboard(effectiveGameId, effectiveEvent);

  const selectedGame = useMemo(
    () => games.find((g) => g.gameConfigId === effectiveGameId),
    [games, effectiveGameId],
  );

  const error = gamesError || lbError;
  const loading = gamesLoading;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          {backToDashboardLink}
          <h1 className="text-2xl font-semibold">Leaderboards</h1>
        </div>
        <Skeleton className="h-10 rounded-lg w-64" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          {backToDashboardLink}
          <h1 className="text-2xl font-semibold">Leaderboards</h1>
        </div>
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          {backToDashboardLink}
          <h1 className="text-2xl font-semibold">Leaderboards</h1>
        </div>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No games with leaderboards available yet.
          </CardContent>
        </Card>
      </div>
    );
  }

  const isHighScore = selectedGame?.leaderboardType === 'high_score';

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        {backToDashboardLink}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Leaderboards</h1>
          <div className="flex items-center gap-2">
            {isHighScore ? (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <ArrowUp className="h-3 w-3" /> High Score
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <ArrowDown className="h-3 w-3" /> Low Score
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-4 flex-wrap">
        <select
          className="rounded-md border bg-background px-3 py-2 text-sm"
          value={effectiveGameId ?? ''}
          onChange={(e) => {
            setSelectedGameId(e.target.value);
            setSelectedEvent(null);
          }}
        >
          {games.map((g) => (
            <option key={g.gameConfigId} value={g.gameConfigId}>
              {g.displayName}
            </option>
          ))}
        </select>
      </div>

      {lbLoading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : leaderboard && leaderboard.entries.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Trophy className="h-4 w-4" /> Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium w-16">Rank</th>
                    <th className="text-left py-2 font-medium">Player</th>
                    <th className="text-right py-2 font-medium">Best Score</th>
                    <th className="text-right py-2 font-medium">Plays</th>
                    <th className="text-right py-2 font-medium">Last Played</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.entries.map((entry) => {
                    const isCurrentUser = entry.userId === user?.userId;
                    return (
                      <tr
                        key={entry.userId}
                        className={`border-b last:border-0 ${isCurrentUser ? 'bg-primary/5 font-medium' : ''}`}
                      >
                        <td className="py-2">
                          {entry.rank <= 3 ? (
                            <span className="text-lg">
                              {entry.rank === 1 && '\uD83E\uDD47'}
                              {entry.rank === 2 && '\uD83E\uDD48'}
                              {entry.rank === 3 && '\uD83E\uDD49'}
                            </span>
                          ) : (
                            `#${entry.rank}`
                          )}
                        </td>
                        <td className="py-2">
                          {entry.username ?? 'Unknown'}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-primary">(you)</span>
                          )}
                        </td>
                        <td className="text-right py-2">{entry.bestScore.toLocaleString()}</td>
                        <td className="text-right py-2 text-muted-foreground">
                          {entry.playCount.toLocaleString()}
                        </td>
                        <td className="text-right py-2 text-muted-foreground">
                          {new Date(entry.lastPlayedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {leaderboard.myRank && !leaderboard.entries.some((e) => e.userId === user?.userId) && (
              <div className="mt-4 p-3 rounded-md bg-muted/50 text-sm">
                Your rank: <strong>#{leaderboard.myRank.rank}</strong> with a score of{' '}
                <strong>{leaderboard.myRank.bestScore.toLocaleString()}</strong>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No leaderboard entries yet for this game event.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
